import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { sendPasswordReset } from "../services/auth";
import "./AuthPage.css";

const PUBLIC_URL = process.env.PUBLIC_URL || "";

type ForgotStep = "email" | "sent";

const TIMER_SECONDS = 60;

const ForgotPasswordPage: React.FC = () => {
  const [step, setStep] = useState<ForgotStep>("email");
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [submittedEmail, setSubmittedEmail] = useState("");
  const [timeLeft, setTimeLeft] = useState(0);
  const [sending, setSending] = useState(false);

  const startTimer = useCallback(() => {
    setTimeLeft(TIMER_SECONDS);
  }, []);

  useEffect(() => {
    if (step !== "sent") return;
    const t = setInterval(() => {
      setTimeLeft((prev) => (prev <= 0 ? 0 : prev - 1));
    }, 1000);
    return () => clearInterval(t);
  }, [step]);

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    setEmailError("");
    if (!email.trim()) {
      setEmailError("Email address is invalid");
      return;
    }
    setSending(true);
    try {
      await sendPasswordReset(email.trim());
      setSubmittedEmail(email.trim());
      setStep("sent");
      startTimer();
    } catch (err: unknown) {
      const code = err && typeof err === "object" && "code" in err ? (err as { code: string }).code : "";
      if (code === "auth/user-not-found") {
        setEmailError("No account found with this email address.");
      } else if (code === "auth/invalid-email") {
        setEmailError("Please enter a valid email address.");
      } else if (err instanceof Error && err.message) {
        setEmailError(err.message);
      } else {
        setEmailError("Something went wrong. Please try again.");
      }
    } finally {
      setSending(false);
    }
  };

  const handleResend = async () => {
    if (timeLeft > 0) return;
    setSending(true);
    try {
      await sendPasswordReset(submittedEmail);
      startTimer();
    } catch {
      setEmailError("Failed to resend. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  };

  const logoUrl = PUBLIC_URL ? `${PUBLIC_URL}/logo.svg` : "/logo.svg";

  const pageTitle = "Forgot Password";

  return (
    <div className="auth-page">
      <div className="auth-card auth-card--forgot">
        <img src={logoUrl} alt="Logo" className="auth-logo" />
        <h1 className="auth-title">{pageTitle}</h1>

        {step === "email" && (
          <>
            <p className="auth-subtitle auth-subtitle--forgot">
              Please enter the email address associated with your account.
              We'll send you instructions on how to reset your password.
            </p>
            <form onSubmit={handleSend}>
              <div className="auth-field">
                <label htmlFor="forgot-email">Email</label>
                <div
                  className={`auth-input-wrapper${emailError ? " auth-input-wrapper--error auth-input-wrapper--has-suffix" : ""}`}
                >
                  <input
                    id="forgot-email"
                    type="email"
                    className="auth-input"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setEmailError("");
                    }}
                  />
                  {emailError && (
                    <span className="auth-input-suffix">
                      <img
                        src={`${PUBLIC_URL}/icon-wrapper.svg`}
                        alt=""
                        aria-hidden
                      />
                    </span>
                  )}
                </div>
                {emailError && (
                  <p className="auth-field-error" role="alert">
                    {emailError}
                  </p>
                )}
              </div>
              <button type="submit" className="auth-btn" disabled={sending}>
                {sending ? "Sending…" : "Send"}
              </button>
            </form>
            <p className="auth-bottom auth-bottom--forgot">
              Back to{" "}
              <Link to="/login" className="auth-link">
                Log in
              </Link>
            </p>
          </>
        )}

        {step === "sent" && (
          <>
            <p className="auth-subtitle auth-subtitle--forgot">
              We've sent password reset instructions to{" "}
              <strong className="auth-email-highlight">{submittedEmail}</strong>
            </p>
            <p className="auth-forgot-prompt">Not in inbox or spam folder?</p>
            <button
              type="button"
              className="auth-btn"
              disabled={timeLeft > 0 || sending}
              onClick={handleResend}
            >
              {sending ? "Sending…" : "Resend link"}
            </button>
            <p className="auth-timer">{formatTime(timeLeft)}</p>
            <p className="auth-bottom auth-bottom--forgot">
              Back to{" "}
              <Link to="/login" className="auth-link">
                Log in
              </Link>
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
